import api from './api'

const githubOAuthService = {
  /**
   * Initiates the GitHub OAuth flow
   * Opens a popup window for user authorization
   * @param {string} returnTo - URL to return to after OAuth completes
   * @returns {Promise<void>}
   */
  async initiateOAuthFlow(returnTo = window.location.href) {
    try {
      const params = returnTo ? { return_to: returnTo } : {}
      const response = await api.get('/github_oauth/authorize', { params })
      const { authorization_url } = response.data

      // Open popup window
      const width = 600
      const height = 700
      const left = window.screen.width / 2 - width / 2
      const top = window.screen.height / 2 - height / 2

      const popup = window.open(
        authorization_url,
        'GitHub OAuth',
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
      )

      if (!popup) {
        throw new Error('Popup blocked. Please allow popups for this site.')
      }

      // Poll for popup closure or message
      return new Promise((resolve, reject) => {
        const checkClosed = setInterval(() => {
          if (popup.closed) {
            clearInterval(checkClosed)
            // Check status after popup closes
            this.getStatus()
              .then((status) => {
                if (status.authorized) {
                  resolve()
                } else {
                  reject(new Error('Authorization was not completed'))
                }
              })
              .catch(reject)
          }
        }, 500)

        // Listen for messages from popup
        const messageHandler = (event) => {
          if (event.data.type === 'github_oauth_success') {
            clearInterval(checkClosed)
            window.removeEventListener('message', messageHandler)
            popup.close()
            resolve()
          } else if (event.data.type === 'github_oauth_error') {
            clearInterval(checkClosed)
            window.removeEventListener('message', messageHandler)
            popup.close()
            reject(new Error(event.data.error || 'OAuth authorization failed'))
          }
        }

        window.addEventListener('message', messageHandler)

        // Timeout after 5 minutes
        setTimeout(() => {
          if (!popup.closed) {
            popup.close()
          }
          clearInterval(checkClosed)
          window.removeEventListener('message', messageHandler)
          reject(new Error('OAuth authorization timed out'))
        }, 300000) // 5 minutes
      })
    } catch (error) {
      console.error('GitHub OAuth flow failed:', error)
      throw error
    }
  },

  /**
   * Get the current OAuth authorization status
   * @returns {Promise<{authorized: boolean, expires_at: string|null, scope: string|null}>}
   */
  async getStatus() {
    const response = await api.get('/github_oauth/status')
    return response.data
  },

  /**
   * Revoke the current OAuth authorization
   * @returns {Promise<void>}
   */
  async revoke() {
    await api.post('/github_oauth/revoke')
  },
}

export default githubOAuthService

